// Wizard step: Vzhled aplikace (branding) - brand colors, the 1024x1024 PNG
// icon and live phone previews (login / signup / chat) drawn in the chosen
// colors, mirroring the reference "Look & feel" step.
import { useState } from "react";
import type { AppBrandingPayload } from "../../../shared/app-wizard";
import {
  AssetUploadZone,
  FieldBlock,
  SaveBadge,
  useStepSaver,
  type StepProps,
  type WizardT
} from "./wizard-shared";

const EMPTY_BRANDING: AppBrandingPayload = {
  colors: { primary: "", secondary: "", accent: "", background: "" },
  iconAttachmentId: null
};

const COLOR_KEYS = ["primary", "secondary", "accent", "background"] as const;

const FALLBACK: Record<(typeof COLOR_KEYS)[number], string> = {
  primary: "#4a90ff",
  secondary: "#00d4ff",
  accent: "#f59e0b",
  background: "#101828"
};

export function StepBranding(props: StepProps) {
  // Empty stored colors are materialized as the BuddyPro defaults so the hex
  // field always shows the SAME color as the swatch next to it. A colored
  // swatch beside a "#000000" placeholder read as a contradiction (Pavel
  // 27.7. - visual bug report).
  const [draft, setDraft] = useState<AppBrandingPayload>(() => {
    const stored = props.data.wizard.steps.branding ?? EMPTY_BRANDING;
    const colors = { ...stored.colors };
    for (const key of COLOR_KEYS) {
      if (!/^#[0-9a-fA-F]{6}$/.test(colors[key])) colors[key] = FALLBACK[key];
    }
    return { ...stored, colors };
  });
  const saver = useStepSaver("branding", props.save, props.readOnly);

  const update = (next: AppBrandingPayload) => {
    setDraft(next);
    saver.queue(next as unknown as Record<string, unknown>);
  };

  const setColor = (key: (typeof COLOR_KEYS)[number], value: string) => {
    update({ ...draft, colors: { ...draft.colors, [key]: value } });
  };

  const effective = (key: (typeof COLOR_KEYS)[number]): string =>
    /^#[0-9a-fA-F]{6}$/.test(draft.colors[key]) ? draft.colors[key] : FALLBACK[key];

  const appName = props.data.wizard.steps.basics?.appName || props.t("appWizard.branding.previewAppName");

  return (
    <div className="appwiz-step">
      <header className="appwiz-step-heading">
        <h2>{props.t("appWizard.branding.title")}</h2>
        <p>{props.t("appWizard.branding.copy")}</p>
        <SaveBadge state={saver.state} t={props.t} />
      </header>

      <FieldBlock label={props.t("appWizard.branding.colorsLabel")} hint={props.t("appWizard.branding.colorsHint")}>
        <div className="appwiz-color-grid">
          {COLOR_KEYS.map((key) => (
            <label className="appwiz-color-field" key={key}>
              <input
                type="color"
                value={effective(key)}
                disabled={props.readOnly}
                aria-label={props.t(`appWizard.branding.color.${key}`)}
                onChange={(event) => setColor(key, event.target.value)}
              />
              <span className="appwiz-color-meta">
                <strong>{props.t(`appWizard.branding.color.${key}`)}</strong>
                <input
                  className="appwiz-color-hex"
                  type="text"
                  value={draft.colors[key]}
                  placeholder={"#000000"}
                  maxLength={7}
                  readOnly={props.readOnly}
                  aria-label={`${props.t(`appWizard.branding.color.${key}`)} hex`}
                  onChange={(event) => setColor(key, event.target.value.trim())}
                />
                <small>{props.t(`appWizard.branding.colorHint.${key}`)}</small>
              </span>
            </label>
          ))}
        </div>
        <div className="appwiz-palette" aria-hidden="true">
          {COLOR_KEYS.map((key) => <span key={key} style={{ background: effective(key) }} />)}
        </div>
      </FieldBlock>

      <FieldBlock label={props.t("appWizard.branding.iconLabel")} hint={props.t("appWizard.branding.iconHint")}>
        <AssetUploadZone
          slot="icon"
          label={props.t("appWizard.branding.iconLabel")}
          attachmentId={draft.iconAttachmentId}
          csrfToken={props.csrfToken}
          readOnly={props.readOnly}
          t={props.t}
          onUploaded={(attachmentId) => update({ ...draft, iconAttachmentId: attachmentId })}
          onCleared={() => update({ ...draft, iconAttachmentId: null })}
        />
      </FieldBlock>

      <FieldBlock label={props.t("appWizard.branding.previewLabel")} hint={props.t("appWizard.branding.previewHint")}>
        <div className="appwiz-phones">
          <PhonePreview kind="login" appName={appName} icon={draft.iconAttachmentId} colors={{
            primary: effective("primary"), secondary: effective("secondary"),
            accent: effective("accent"), background: effective("background")
          }} t={props.t} />
          <PhonePreview kind="signup" appName={appName} icon={draft.iconAttachmentId} colors={{
            primary: effective("primary"), secondary: effective("secondary"),
            accent: effective("accent"), background: effective("background")
          }} t={props.t} />
          <PhonePreview kind="chat" appName={appName} icon={draft.iconAttachmentId} colors={{
            primary: effective("primary"), secondary: effective("secondary"),
            accent: effective("accent"), background: effective("background")
          }} t={props.t} />
        </div>
      </FieldBlock>
    </div>
  );
}

/** Miniature app screen rendered live in the chosen colors (no images). */
function PhonePreview(props: {
  kind: "login" | "signup" | "chat";
  appName: string;
  icon: string | null;
  colors: { primary: string; secondary: string; accent: string; background: string };
  t: WizardT;
}) {
  const { colors } = props;
  const iconNode = props.icon
    ? <img className="appwiz-phone-icon" src={`/api/attachments/${props.icon}`} alt="" />
    : <span className="appwiz-phone-icon appwiz-phone-icon-fallback" style={{ background: colors.primary }}>{props.appName.slice(0, 1).toUpperCase() || "A"}</span>;

  return (
    <figure className="appwiz-phone" style={{ background: colors.background }}>
      <span className="appwiz-phone-notch" aria-hidden="true" />
      {props.kind !== "chat" ? (
        <div className="appwiz-phone-body">
          {iconNode}
          <strong style={{ color: colors.primary }}>{props.kind === "login" ? props.appName : props.t("appWizard.branding.previewSignupTitle")}</strong>
          <small>{props.t(props.kind === "login" ? "appWizard.branding.previewLoginSub" : "appWizard.branding.previewSignupSub")}</small>
          {(props.kind === "login" ? [1, 2] : [1, 2, 3]).map((row) => (
            <span className="appwiz-phone-input" key={row} />
          ))}
          <span className="appwiz-phone-button" style={{ background: colors.primary }}>
            {props.t(props.kind === "login" ? "appWizard.branding.previewLoginCta" : "appWizard.branding.previewSignupCta")}
          </span>
          <small style={{ color: colors.accent }}>{props.t("appWizard.branding.previewLink")}</small>
        </div>
      ) : (
        <div className="appwiz-phone-body appwiz-phone-chat">
          <span className="appwiz-phone-chat-head">
            {iconNode}
            <span>
              <strong>{props.appName}</strong>
              <small style={{ color: colors.secondary }}>● {props.t("appWizard.branding.previewOnline")}</small>
            </span>
          </span>
          <span className="appwiz-phone-bubble left">{props.t("appWizard.branding.previewChatBot")}</span>
          <span className="appwiz-phone-bubble right" style={{ background: colors.primary }}>{props.t("appWizard.branding.previewChatUser")}</span>
          <span className="appwiz-phone-bubble left">{props.t("appWizard.branding.previewChatBot2")}</span>
          <span className="appwiz-phone-compose">
            <span />
            <span className="appwiz-phone-send" style={{ background: colors.accent }} />
          </span>
        </div>
      )}
      <figcaption>{props.t(`appWizard.branding.previewCaption.${props.kind}`)}</figcaption>
    </figure>
  );
}
